import { useSetAtom } from 'jotai';

import { ActionItemStatus } from '@ekhodealer/ekho-common';
import { Box, Button, ContentWithTooltip, Group, Stack, Text } from '@ekhodealer/ekho-common/rev';

import type { ActionItem} from '@ekhodealer/ekho-common';
import type { ReactNode } from 'react';

import ChevronRight24 from '@common/components/icons/ChevronRight24';
import CompletedActionItem24 from '@common/components/icons/CompletedActionItem24';
import PendingActionItem24 from '@common/components/icons/PendingActionItem24';
import PendingActionItemOther24 from '@common/components/icons/PendingActionItemOther24';
import QueuedActionItem24 from '@common/components/icons/QueuedActionItem24';
import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { formatTimestampToDateTime } from '@common/lib/time';

import { navigateToActionItemAtom } from '../../atoms/orderDetailsDrawerAtoms';

interface ActionItemViewProps {
	/** The action item to display */
	actionItem: ActionItem;
	/** The resolved agent name for this action item */
	agentName: string;
}

/**
 * Get the status icon for an action item based on its status
 */
const getStatusIcon = (
	status: ActionItemStatus,
	agentId: string | undefined,
	currentServicesProviderId: string | null,
) => {
	switch (status) {
		case ActionItemStatus.COMPLETE:
			return <CompletedActionItem24 />;
		case ActionItemStatus.PENDING:
			return agentId === currentServicesProviderId ? (
				<PendingActionItem24 />
			) : (
				<PendingActionItemOther24 />
			);
		case ActionItemStatus.QUEUED:
			return <QueuedActionItem24 />;
		default:
			return <QueuedActionItem24 />;
	}
};

const getPrimaryLabel = (actionItem: ActionItem): ReactNode => {
	return (
		<Text
			size='bodySmall'
			weight='semibold'
			color={actionItem.status === ActionItemStatus.QUEUED ? 'secondary' : undefined}>
			{actionItem.config.title}
		</Text>
	);
};

/**
 * Get the tooltip text for an action item based on its status
 */
const getStatusTooltipText = (status: ActionItemStatus, agentLabel: string): string => {
	switch (status) {
		case ActionItemStatus.COMPLETE:
			return 'This item is complete.';
		case ActionItemStatus.PENDING:
			return `This item is pending. Waiting for ${agentLabel} to complete this action.`;
		case ActionItemStatus.QUEUED:
			return 'This item is queued. It will become available later as the order progresses.';
		default:
			return '';
	}
};

/**
 * Determine if a button should be shown for this action item
 * Returns the button component if one should be displayed, otherwise null
 */
const getActionItemButton = (
	actionItem: ActionItem,
	currentServicesProviderId: string | null,
	onNavigate: () => void,
): ReactNode | null => {
	// Show button if buttonTitle exists, status is PENDING or QUEUED, and agentId matches current services provider
	// Button is disabled for QUEUED items
	if (
		actionItem.config.buttonTitle &&
		(actionItem.status === ActionItemStatus.PENDING ||
			actionItem.status === ActionItemStatus.QUEUED) &&
		actionItem.agentId === currentServicesProviderId
	) {
		const isDisabled = actionItem.status === ActionItemStatus.QUEUED;
		return (
			<Button size='medium' onPress={onNavigate} isDisabled={isDisabled}>
				{actionItem.config.buttonTitle}
			</Button>
		);
	}

	// Show chevron icon button for completed action items
	if (
		actionItem.status === ActionItemStatus.COMPLETE &&
		actionItem.agentId === currentServicesProviderId
	) {
		return (
			<Button
				variant='ghost'
				size='medium'
				onPress={onNavigate}
				icon={<ChevronRight24 />}
				aria-label={`View details for ${actionItem.config.title}`}
			/>
		);
	}

	return null;
};

/**
 * ActionItemView component that displays a single action item with:
 * - Status icon on the left
 * - Title with agent label next to it
 * - Completion date below the title
 * - Right accessory (chevron or button)
 */
export const ActionItemView = ({ actionItem, agentName }: ActionItemViewProps) => {
	const currentServicesProviderId = useCurrentServicesProviderId();
	const navigateToActionItem = useSetAtom(navigateToActionItemAtom);

	// Handle navigation to action item detail
	const handleNavigate = () => {
		navigateToActionItem(actionItem);
	};

	const statusIcon = getStatusIcon(
		actionItem.status,
		actionItem.agentId,
		currentServicesProviderId,
	);
	const statusTooltipText = getStatusTooltipText(actionItem.status, agentName);
	const completionDate = formatTimestampToDateTime(actionItem.completedAt);
	const rightAccessory = getActionItemButton(
		actionItem,
		currentServicesProviderId,
		handleNavigate,
	);

	return (
		<Box rounding='12' background='secondary' padding='16'>
			<Group itemsAlignX='space-between' itemsSpacing='12' itemsAlignY='center' noWrap>
				<Group.Item>
					<ContentWithTooltip
						content={<div>{statusIcon}</div>}
						tooltipContent={statusTooltipText}
					/>
				</Group.Item>
				<Group.Item grow={1}>
					<Stack itemsSpacing={2}>
						<Group itemsSpacing='8' itemsAlignY='center' itemsAlignX='start' noWrap>
							{getPrimaryLabel(actionItem)}
							<Text size='footnote' color='secondary'>
								{agentName}
							</Text>
						</Group>
						{completionDate && (
							<Text size='footnote' color='secondary'>
								Completed {completionDate}
							</Text>
						)}
					</Stack>
				</Group.Item>
				{rightAccessory && <Group.Item>{rightAccessory}</Group.Item>}
			</Group>
		</Box>
	);
};
